import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Zap as ZapIcon, Plus, Settings, LogOut, MessageSquareText, LayoutDashboard, Car, Menu, ChevronRight, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import DateRangePicker from "@/components/DateRangePicker";
import { useAuth } from "@/lib/auth";

export default function Header({
  vehicles,
  selectedVehicleId,
  onVehicleChange,
  usageType = "all",
  onUsageChange,
  dateRange,
  onDateRangeChange,
  onAddData,
  onOpenSettings,
  onOpenFeedback,
  showFilters = true,
}) {
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = () => setMobileNavOpen(false);
  const initials = (user?.name || user?.email || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-[var(--bg-deep)]/85 border-b border-[#161616]"
      data-testid="app-header"
    >
      <div className="px-5 sm:px-6 lg:px-10 py-3 lg:py-4 flex flex-wrap items-center gap-2 lg:gap-4">
        {/* Brand */}
        <Link
          to="/dashboard"
          className="flex items-center gap-3 tap rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E82127]/60"
          aria-label="Go to dashboard"
          data-testid="brand-home-link"
        >
          <div className="relative w-9 h-9 bg-[var(--bg-inverse)] flex items-center justify-center" style={{ borderRadius: 2 }}>
            <ZapIcon className="w-5 h-5 text-[var(--text-inverse)]" strokeWidth={2.25} />
            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#E82127] rounded-full" />
          </div>
          <div className="leading-tight flex-shrink min-w-0 max-w-[125px] sm:max-w-none">
            <div className="font-display text-base font-medium tracking-tight">MileageIQ</div>
            <div className="label-tiny">Console (BETA)</div>
          </div>
        </Link>

        {/* Nav */}
        {/* Desktop / Tablet Nav */}
        <nav className="hidden md:flex items-center gap-0.5 ml-1 lg:ml-4" data-testid="main-nav">
          <Link
            to="/dashboard"
            data-testid="nav-dashboard"
            className={`nav-underline px-2 lg:px-3 py-1.5 text-sm tracking-tight transition-colors ${
              pathname === "/dashboard" ? "text-[var(--text-primary)] active" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            Dashboard
          </Link>
          <Link
            to="/vehicles"
            data-testid="nav-vehicles"
            className={`nav-underline px-2 lg:px-3 py-1.5 text-sm tracking-tight transition-colors ${
              pathname.startsWith("/vehicles") ? "text-[var(--text-primary)] active" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            Vehicles
          </Link>
          <Link
            to="/maintenance"
            data-testid="nav-maintenance"
            className={`nav-underline px-2 lg:px-3 py-1.5 text-sm tracking-tight transition-colors ${
              pathname.startsWith("/maintenance") ? "text-[var(--text-primary)] active" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            Maintenance
          </Link>
        </nav>

        {/* Mobile hamburger removed — nav lives inside the avatar dropdown below (see md:hidden items). */}

        {/* Spacer (desktop) — pushes right cluster to the end */}
        <div className="hidden lg:block ml-auto" />


        {/* Filters group — wraps to its own row on mobile.
            The Vehicle Context dropdown only appears when the user has >1
            active vehicle. Single-vehicle households see a cleaner header. */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto lg:contents order-4 lg:order-none mt-3 lg:mt-0">
            <div className="hidden lg:block w-px h-6 bg-[var(--bg-hover)] mx-1" />
            {vehicles.length > 1 && (
              <Select value={selectedVehicleId || "all"} onValueChange={onVehicleChange}>
                <SelectTrigger
                  className="w-[150px] sm:w-[170px] bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)] h-9"
                  data-testid="vehicle-select"
                >
                  <SelectValue placeholder="Vehicle" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)]">
                  <SelectItem value="all" data-testid="vehicle-option-all">All Vehicles</SelectItem>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id} data-testid={`vehicle-option-${v.id}`}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={usageType || "all"} onValueChange={onUsageChange}>
              <SelectTrigger
                className="w-[125px] sm:w-[140px] bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)] h-9"
                data-testid="usage-select"
              >
                <SelectValue placeholder="Usage" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)]">
                <SelectItem value="all" data-testid="usage-option-all">All Usage</SelectItem>
                <SelectItem value="personal" data-testid="usage-option-personal">Personal</SelectItem>
                <SelectItem value="business" data-testid="usage-option-business">Business</SelectItem>
              </SelectContent>
            </Select>
            <DateRangePicker value={dateRange} onChange={onDateRangeChange} />
          </div>
        )}

        {/* Action button — single primary entry point for ALL data input */}
        {showFilters && onAddData && (
          <div className="hidden md:flex items-center gap-2 w-full lg:w-auto justify-end order-5 lg:order-none">
            <Button
              onClick={onAddData}
              className="tap bg-[#E82127] text-white hover:bg-[#FF2A30] shadow-[0_0_0_0_rgba(232,33,39,0)] hover:shadow-[0_0_24px_-6px_rgba(232,33,39,0.6)] h-9"
              style={{ borderRadius: 2 }}
              data-testid="add-data-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.25} />
              Add Data
            </Button>
          </div>
        )}
      
        {/* Right cluster: feedback + settings + user menu (always visible) */}
        <div className="flex items-center gap-2 ml-auto order-2 lg:order-none">
          {onOpenFeedback && (
            <Button
              onClick={onOpenFeedback}
              variant="outline"
              size="icon"
              className="tap hidden md:inline-flex bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-muted)] hover:bg-[var(--bg-emphasis)] hover:text-[var(--text-primary)] hover:border-[#262626] h-9 w-9"
              style={{ borderRadius: 2 }}
              data-testid="feedback-btn"
              title="Send feedback"
              aria-label="Send feedback"
            >
              <MessageSquareText className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          )}
          {onOpenSettings && (
            <Button
              onClick={onOpenSettings}
              variant="outline"
              size="icon"
              className="hidden md:inline-flex tap bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-muted)] hover:bg-[var(--bg-emphasis)] hover:text-[var(--text-primary)] hover:border-[#262626] h-9 w-9"
              style={{ borderRadius: 2 }}
              data-testid="settings-btn"
              title="Settings"
            >
              <Settings className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          )}
          {user && (
            <>
              {/* --- Mobile hamburger --- surfaces the primary nav so users
                  don't have to discover that the avatar is a menu trigger. */}
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open menu"
                className="md:hidden tap relative h-10 w-10 flex items-center justify-center text-[var(--text-primary)]"
                style={{ borderRadius: 2 }}
                data-testid="mobile-nav-toggle"
              >
                <Menu className="w-5 h-5" strokeWidth={1.75} />
              </button>

              {/* --- Desktop avatar dropdown (unchanged) --- */}
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="hidden md:flex tap relative h-9 w-9 overflow-hidden bg-[var(--bg-elevated)] border border-[var(--border-hover)] hover:border-[#262626] transition-colors items-center justify-center"
                  style={{ borderRadius: 999 }}
                  data-testid="user-menu-btn"
                  title={user.email}
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="text-[11px] font-mono-data tracking-wider text-[var(--text-primary)]">
                      {initials || "U"}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)] min-w-[220px]"
                data-testid="user-menu"
              >
                <DropdownMenuLabel className="font-normal px-3 py-3">
                  <div className="text-sm font-medium tracking-tight">
                    {user.name}
                  </div>

                  <div className="mt-1 text-[11px] text-[var(--text-muted)] truncate">
                    {user.email}
                  </div>
                </DropdownMenuLabel>
                {onOpenSettings && (
                  <DropdownMenuItem
                    onClick={onOpenSettings}
                    className="cursor-pointer focus:bg-[var(--bg-emphasis)] focus:text-[var(--text-primary)]"
                    data-testid="user-menu-settings-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Settings
                  </DropdownMenuItem>
                )}
                {onOpenFeedback && (
                  <DropdownMenuItem
                    onClick={onOpenFeedback}
                    className="cursor-pointer focus:bg-[var(--bg-emphasis)] focus:text-[var(--text-primary)]"
                    data-testid="user-menu-feedback-btn"
                  >
                    <MessageSquareText className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Send feedback
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={signOut}
                  className="cursor-pointer focus:bg-[var(--bg-emphasis)] focus:text-[var(--text-primary)] text-[#FF5A5F] focus:text-[#FF5A5F]"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </div>


      </div>

      {/* ============================================================
           Mobile navigation drawer — right-side slide-out sheet.
           Mirrors the VehicleDetailSheet pattern so the app feels
           consistent.
         ============================================================ */}
      {user && (
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent
            side="right"
            className="w-[320px] sm:w-[360px] bg-[var(--bg-elevated)] border-l border-[var(--border-hover)] p-0 flex flex-col"
            data-testid="mobile-nav-sheet"
          >
            <SheetHeader className="px-5 pt-5 pb-4 border-b border-[var(--border-hover)] space-y-0">
              <div className="flex items-center gap-2.5">
                <div
                  className="relative w-8 h-8 bg-[var(--bg-inverse)] flex items-center justify-center shrink-0"
                  style={{ borderRadius: 2 }}
                  aria-hidden="true"
                >
                  <ZapIcon className="w-4 h-4 text-[var(--text-inverse)]" strokeWidth={2.25} />
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#E82127] rounded-full" />
                </div>
                <SheetTitle className="text-left leading-tight text-[var(--text-primary)]">
                  <div className="font-display text-base font-medium tracking-tight">MileageIQ</div>
                  <div className="label-tiny">Console (BETA)</div>
                </SheetTitle>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
              {/* NAVIGATION */}
              <div>
                <div className="label-tiny mb-3">Navigation</div>
                <nav className="space-y-1" aria-label="Primary">
                  {[
                    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "mobile-nav-dashboard" },
                    { to: "/vehicles", label: "Vehicles", icon: Car, testid: "mobile-nav-vehicles" },
                    { to: "/maintenance", label: "Maintenance", icon: Wrench, testid: "mobile-nav-maintenance" },
                  ].map(({ to, label, icon: Icon, testid }) => {
                    const active = pathname === to || pathname.startsWith(`${to}/`);
                    return (
                      <Link
                        key={to}
                        to={to}
                        onClick={closeMobileNav}
                        className={`tap flex items-center gap-3 px-2 py-2.5 text-sm transition-colors ${
                          active
                            ? "text-[#E82127]"
                            : "text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                        }`}
                        style={{ borderRadius: 2 }}
                        data-testid={testid}
                        aria-current={active ? "page" : undefined}
                      >
                        <Icon
                          className={`w-4 h-4 ${active ? "text-[#E82127]" : "text-[var(--text-muted)]"}`}
                          strokeWidth={1.75}
                        />
                        <span className={active ? "font-medium" : ""}>{label}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* APP */}
              <div>
                <div className="label-tiny mb-3">App</div>
                <div className="space-y-1">
                  {onOpenSettings && (
                    <button
                      type="button"
                      onClick={() => { closeMobileNav(); onOpenSettings(); }}
                      className="tap w-full flex items-center gap-3 px-2 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors text-left"
                      style={{ borderRadius: 2 }}
                      data-testid="mobile-nav-settings"
                    >
                      <Settings className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.75} />
                      Settings
                    </button>
                  )}
                  {onOpenFeedback && (
                    <button
                      type="button"
                      onClick={() => { closeMobileNav(); onOpenFeedback(); }}
                      className="tap w-full flex items-center gap-3 px-2 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors text-left"
                      style={{ borderRadius: 2 }}
                      data-testid="mobile-nav-feedback"
                    >
                      <MessageSquareText className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.75} />
                      Send feedback
                    </button>
                  )}
                </div>
              </div>

              {/* ACCOUNT */}
              <div>
                <div className="label-tiny mb-3">Account</div>
                <button
                  type="button"
                  onClick={() => { closeMobileNav(); signOut(); }}
                  className="tap w-full flex items-center gap-3 px-2 py-2.5 text-sm text-[#FF5A5F] hover:bg-[#E82127]/10 transition-colors text-left"
                  style={{ borderRadius: 2 }}
                  data-testid="mobile-nav-signout"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.75} />
                  Sign out
                </button>
              </div>
            </div>

            {/* Profile footer — mirrors the reference mockup. Static
                identity indicator (not tappable) so users don't confuse
                it with the trigger. */}
            <div className="px-5 py-4 border-t border-[var(--border-hover)] flex items-center gap-3">
              <div
                className="relative h-9 w-9 shrink-0 overflow-hidden bg-[var(--bg-canvas)] border border-[var(--border-hover)] flex items-center justify-center"
                style={{ borderRadius: 999 }}
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="text-[11px] font-mono-data tracking-wider text-[var(--text-primary)]">
                    {initials || "U"}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[var(--text-primary)] truncate">{user.name}</div>
                <div className="text-[11px] text-[var(--text-muted)] truncate">{user.email}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[var(--text-faint)] shrink-0" strokeWidth={1.5} aria-hidden="true" />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </header>
  );
}
